import { useMemo } from 'react';
import type { ConversationTurn } from '@/types/backend';

// 费用计算函数
export function calculateCost(data: any): number {
  if (!data || !data.input_token_details || !data.output_token_details) {
    return 0;
  }

  const input = data.input_token_details;
  const output = data.output_token_details;

  // 计算输入费用
  const textNonCached = input.text_tokens - (input.cached_tokens_details?.text_tokens || 0);
  const textCached = input.cached_tokens_details?.text_tokens || 0;
  const audioNonCached = input.audio_tokens - (input.cached_tokens_details?.audio_tokens || 0);
  const audioCached = input.cached_tokens_details?.audio_tokens || 0;

  const inputCost = (
    (textNonCached * 5) +      // 非缓存文本
    (textCached * 2.5) +       // 缓存文本
    (audioNonCached * 40) +    // 非缓存音频
    (audioCached * 2.5)        // 缓存音频
  ) / 1e6;  // 转换为美元

  // 计算输出费用
  const outputCost = (
    (output.text_tokens * 20) + 
    (output.audio_tokens * 80)
  ) / 1e6;

  return inputCost + outputCost;
}

// 简化的费用计算，基于基础token数计算近似费用
export function calculateSimpleCost(totalTokens: number): number {
  // 使用平均费率估算: 约 $10 per million tokens
  return (totalTokens * 10) / 1e6;
}

export function formatCost(cost: number): string {
  if (cost === 0) return '$0.00';
  if (cost < 0.01) return `$${cost.toFixed(2)}`;
  return `$${cost.toFixed(3)}`;
}

export const useConversationCostHook = (conversationTurns: ConversationTurn[]) => {

  // 计算累加后的费用和token数
  const { totalCost, totalTokens } = useMemo(() => {

    if (conversationTurns === undefined || conversationTurns.length === 0) {
      return { totalCost: 0, totalTokens: 0 };
    }

    // 累加所有turn的usage字段
    const accumulatedUsage = {
      total_tokens: 0,
      input_tokens: 0,
      output_tokens: 0,
      input_token_details: {
        text_tokens: 0,
        audio_tokens: 0,
        cached_tokens: 0,
        cached_tokens_details: {
          text_tokens: 0,
          audio_tokens: 0
        }
      },
      output_token_details: {
        text_tokens: 0,
        audio_tokens: 0
      }
    };

    // 遍历所有turn，累加usage字段
    conversationTurns.forEach(turn => {
      const usage = turn.usage_data ? JSON.parse(turn.usage_data) : turn.usage;
      if (usage) {
        accumulatedUsage.total_tokens += usage.total_tokens || 0;
        accumulatedUsage.input_tokens += usage.input_tokens || 0;
        accumulatedUsage.output_tokens += usage.output_tokens || 0;

        // 累加 input_token_details
        if (usage.input_token_details) {
          const inputDetails = usage.input_token_details;
          accumulatedUsage.input_token_details.text_tokens += inputDetails.text_tokens || 0;
          accumulatedUsage.input_token_details.audio_tokens += inputDetails.audio_tokens || 0;
          accumulatedUsage.input_token_details.cached_tokens += inputDetails.cached_tokens || 0;

          // 累加 cached_tokens_details
          if (inputDetails.cached_tokens_details) {
            accumulatedUsage.input_token_details.cached_tokens_details.text_tokens += 
              inputDetails.cached_tokens_details.text_tokens || 0;
            accumulatedUsage.input_token_details.cached_tokens_details.audio_tokens += 
              inputDetails.cached_tokens_details.audio_tokens || 0;
          }
        }

        // 累加 output_token_details
        if (usage.output_token_details) {
          const outputDetails = usage.output_token_details;
          accumulatedUsage.output_token_details.text_tokens += outputDetails.text_tokens || 0;
          accumulatedUsage.output_token_details.audio_tokens += outputDetails.audio_tokens || 0;
        }
      }
    });

    // 使用累加后的usage对象计算总费用
    const calculatedTotalCost = calculateCost(accumulatedUsage);

    return { 
      totalCost: calculatedTotalCost, 
      totalTokens: accumulatedUsage.total_tokens 
    };
  }, [conversationTurns]);

  // 获取格式化的费用字符串
  const getFormattedTotalCost = (): string => {
    return formatCost(totalCost);
  };

  return {
    totalCost,
    totalTokens,
    getFormattedTotalCost,
  };
}; 